import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { KeyValue } from '@mews/enums';
import {
    ANIMATION_DURATION,
    IconName,
    ImageAmount,
    Lightbox as LightboxComponent,
    useInterval,
} from '@optimus-web/core';
import {
    CarouselButtonElement,
    CarouselContainer,
    CarouselImageElement,
    CarouselImageWrapperElement,
    CarouselIndexIndicatorWrapperElement,
    CarouselSwipeableElement,
    CarouselTransitionGroupElement,
    CarouselWrapperElement,
    IndexIndicatorWrapperElement,
    IndicatorDotElement,
} from './style';
import { CarouselProps } from './types';

const DEFAULT_LOADER_DELAY = 100;
const DEFAULT_AUTOSCROLL_INTERVAL = 3000;

export const carouselFactory = ({ CarouselImage = CarouselImageElement, Lightbox = LightboxComponent } = {}) => {
    const Carousel = ({
        disabled = false,
        small = false,
        images,
        initialImageId,
        shouldShowLoader = true,
        lightboxPortalContainerId,
        loaderDelay = DEFAULT_LOADER_DELAY,
        isAutoScrolling,
        autoScrollInterval = DEFAULT_AUTOSCROLL_INTERVAL,
        onCarouselImageClick,
        lightboxEnabled = true,
        imagePlaceholderProps,
        ariaLabel,
        closeIconButtonLabel,
        prevIconButtonLabel,
        nextIconButtonLabel,
        ...other
    }: CarouselProps) => {
        const imageIds = useMemo(() => images.map((image) => image.id), [images]);

        const [imageIndex, setImageIndex] = useState(
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            (initialImageId && imageIds.includes(initialImageId) && imageIds.indexOf(initialImageId)) || 0
        );
        const [isNavigationActive, setNavigationActive] = useState(false);
        const [isLightboxActive, setLightboxActive] = useState(false);
        const [isSlidingForward, setSlidingForward] = useState(true);
        const [isAnimationInProgress, setAnimationInProgress] = useState(false);

        const containerRef = useRef<HTMLDivElement>(null);

        const resetAutoscrollInterval = useInterval({
            handler: selectNextImage,
            timeout: autoScrollInterval,
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            isEnabled: !!isAutoScrolling && !disabled && !isLightboxActive && !isNavigationActive,
        });

        const resetAutoscrollIntervalIfExists = () => {
            if (isAutoScrolling === true) {
                resetAutoscrollInterval();
            }
        };

        function selectNextImage() {
            if (!isAnimationInProgress) {
                setSlidingForward(true);
                setImageIndex((imageIndex + 1) % images.length);
            }
        }

        function selectPreviousImage() {
            if (!isAnimationInProgress) {
                setSlidingForward(false);
                setImageIndex((imageIndex - 1 + images.length) % images.length);
            }
        }

        function selectImageWithId(id: string) {
            const newIndex = imageIds.indexOf(id);
            if (newIndex !== -1) {
                setSlidingForward(newIndex > imageIndex);
                setImageIndex(newIndex);
            }
        }

        const handleRightButtonClick = () => {
            selectNextImage();
            resetAutoscrollIntervalIfExists();
        };

        const handleLeftButtonClick = () => {
            selectPreviousImage();
            resetAutoscrollIntervalIfExists();
        };

        const handleDotClick = (id: string) => {
            selectImageWithId(id);
            resetAutoscrollIntervalIfExists();
        };

        const handleDotKeyDown = (event: KeyboardEvent, id: string) => {
            if (event.key === KeyValue.Enter) {
                handleDotClick(id);
            }
        };

        const handleEnterTransitionCallback = () => {
            setAnimationInProgress(true);
        };

        const handleExitedTransitionCallback = () => {
            setAnimationInProgress(false);
        };

        const showLightbox = (event: React.MouseEvent) => {
            event.preventDefault();
            if (!lightboxEnabled) {
                return;
            }

            if (!disabled && onCarouselImageClick) {
                onCarouselImageClick(event, isLightboxActive);
            }
            if (!disabled) {
                setLightboxActive(true);
            }
        };

        const closeLightbox = (lightboxImageIndex: number) => {
            setImageIndex(lightboxImageIndex);
            setLightboxActive(false);

            if (containerRef.current) {
                containerRef.current.focus();
            }
        };

        const showNavigationElements = () => {
            if (!disabled) {
                setNavigationActive(true);
            }
        };

        const hideNavigationElements = () => {
            if (!disabled) {
                setNavigationActive(false);
            }
        };

        const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
            if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as HTMLElement)) {
                hideNavigationElements();
            }
        };

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const { id, ...imageProps } = images[imageIndex] || {
            id: undefined,
            altOpener: '',
            placeholderProps: imagePlaceholderProps,
        };
        const isEnabledWithMoreImages = !disabled && images.length > 1;

        const wrapperProps = {
            grayscale: disabled,
            onMouseEnter: showNavigationElements,
            onMouseLeave: hideNavigationElements,
            onFocus: showNavigationElements,
            onBlur: handleBlur,
        };

        const carouselTransitionGroupProps = {
            timeoutEnter: 0,
            timeoutExit: ANIMATION_DURATION.default,
            childrenProps: { isSlidingForward },
            onEnter: handleEnterTransitionCallback,
            onExited: handleExitedTransitionCallback,
        };

        const carouselImageProps = {
            ...imageProps,
            disabled,
            shouldShowLoader,
            loaderDelay,
            alt: imageProps.altOpener,
        };

        const buttonBaseProps = {
            active: isNavigationActive,
        };

        const leftButtonProps = {
            ...buttonBaseProps,
            icon: IconName.ChevronLeft,
            onClick: handleLeftButtonClick,
            accessibleText: prevIconButtonLabel,
        };

        const rightButtonProps = {
            ...buttonBaseProps,
            right: true,
            icon: IconName.ChevronRight,
            onClick: handleRightButtonClick,
            accessibleText: nextIconButtonLabel,
        };

        const imageAmountProps = {
            onClick: showLightbox,
            active: isNavigationActive,
            amount: images.length,
        };

        return (
            <CarouselContainer ref={containerRef} aria-disabled={disabled} {...other}>
                <CarouselWrapperElement aria-label={ariaLabel} role="region" {...wrapperProps}>
                    {isLightboxActive && (
                        <Lightbox
                            data-test-lightbox-carousel
                            images={images}
                            initialImageId={id}
                            portalContainerId={lightboxPortalContainerId}
                            onClose={closeLightbox}
                            small={small}
                            closeIconButtonLabel={closeIconButtonLabel}
                            prevIconButtonLabel={prevIconButtonLabel}
                            nextIconButtonLabel={nextIconButtonLabel}
                        />
                    )}
                    {isEnabledWithMoreImages && !small && (
                        <>
                            <CarouselButtonElement
                                variant="primary"
                                data-test-slide-previous
                                type="button"
                                {...leftButtonProps}
                            />
                            <CarouselButtonElement
                                variant="primary"
                                data-test-slide-next
                                type="button"
                                {...rightButtonProps}
                            />
                        </>
                    )}
                    <CarouselSwipeableElement onSwipedLeft={selectNextImage} onSwipedRight={selectPreviousImage}>
                        <CarouselTransitionGroupElement {...carouselTransitionGroupProps}>
                            <CarouselImageWrapperElement
                                key={id}
                                href={carouselImageProps.src}
                                onClick={showLightbox}
                                data-test-image={id}
                                aria-live="polite"
                            >
                                <CarouselImage {...carouselImageProps} />
                            </CarouselImageWrapperElement>
                        </CarouselTransitionGroupElement>
                    </CarouselSwipeableElement>
                    {!disabled && !small && lightboxEnabled && <ImageAmount {...imageAmountProps} />}
                    {isEnabledWithMoreImages && (
                        <CarouselIndexIndicatorWrapperElement>
                            <IndexIndicatorWrapperElement>
                                {imageIds.map((key) => (
                                    <IndicatorDotElement
                                        onClick={() => handleDotClick(key)}
                                        onKeyDown={(e) => handleDotKeyDown(e, key)}
                                        key={key}
                                        active={key === id}
                                        tabIndex={0}
                                    />
                                ))}
                            </IndexIndicatorWrapperElement>
                        </CarouselIndexIndicatorWrapperElement>
                    )}
                </CarouselWrapperElement>
            </CarouselContainer>
        );
    };

    return Carousel;
};

export const Carousel = carouselFactory();
